import React, { useState, useEffect, useCallback } from 'react';
import { getRoles, deleteRol } from '../services/rolService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Search, ShieldCheck } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function RolesList() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();

    const cargarRoles = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getRoles();
            setRoles(data);
            setError(null);
        } catch (error) {
            console.error('Error al cargar roles:', error);
            setError('No se pudieron cargar los roles.');
            toast.error('Error al cargar los roles');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarRoles();
    }, [cargarRoles]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este rol?')) {
            try {
                await deleteRol(id);
                toast.success('Rol eliminado exitosamente');
                cargarRoles();
            } catch (error) {
                console.error('Error al eliminar:', error);
                toast.error('No se pudo eliminar el rol. Verifique si tiene usuarios asignados.');
            }
        }
    };

    const rolesFiltrados = roles.filter(rol =>
        rol.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rol.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/seguridad')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver al Panel de Seguridad"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-blue-400" />
                        <h1 className="text-lg font-semibold tracking-tight">Seguridad: Roles del Sistema</h1>
                    </div>
                </div>

                <Link
                    to="/seguridad/roles/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVO ROL
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <div className="text-sm text-gray-500 font-medium italic">
                    Gestiona los grupos de permisos para los operadores.
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar rol..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm bg-white"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative min-h-[400px]">
                {error && (
                    <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-xs uppercase tracking-wider font-bold">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 text-center">ID</th>
                                <th className="px-4 py-3 border-r border-gray-200">Nombre del Rol</th>
                                <th className="px-4 py-3 border-r border-gray-200">Descripción</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-32 text-center">Permisos</th>
                                <th className="px-4 py-3 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-600">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                                            <span className="text-blue-600 font-medium">Cargando roles...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : rolesFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center text-gray-500 flex flex-col items-center gap-2">
                                        <Search className="w-8 h-8 text-gray-300" />
                                        <span className="italic">
                                            {searchTerm
                                                ? `No se encontraron resultados para "${searchTerm}"`
                                                : 'No hay roles registrados en el sistema.'}
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                rolesFiltrados.map((rol) => (
                                    <tr key={rol.id} className="border-b border-gray-200 hover:bg-[#f1f5f9] transition-colors group">
                                        <td className="px-4 py-3 border-r border-gray-200 text-center font-mono text-xs text-gray-500">
                                            {rol.id}
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200">
                                            <span className="font-bold text-gray-800 uppercase block leading-tight">{rol.nombre}</span>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200">
                                            <span className="text-gray-500 text-xs italic">{rol.descripcion || 'Sin descripción'}</span>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 text-center">
                                            <span className="px-2 py-1 bg-blue-50 text-blue-700 font-bold text-xs rounded-full border border-blue-100">
                                                {rol.permisoIds?.length || 0}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center items-center gap-3">
                                                <Link
                                                    to={`/seguridad/roles/${rol.id}/editar`}
                                                    className="text-blue-600 hover:text-blue-800 font-bold text-[0.7rem] tracking-widest uppercase transition-all"
                                                >
                                                    EDITAR
                                                </Link>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    onClick={() => handleEliminar(rol.id)}
                                                    className="text-red-500 hover:text-red-700 font-bold text-[0.7rem] tracking-widest uppercase transition-all"
                                                >
                                                    ELIMINAR
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 font-medium">
                Total: {rolesFiltrados.length} roles configurados
            </div>
        </div>
    );
}
